import type { ReactElement } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { AppLayout } from './AppLayout';
import { RequireAuth } from './RequireAuth';
import { RootLayout } from './RootLayout';
import { LoginPage } from '../pages/LoginPage';
import { SupportPage } from '../pages/SupportPage';
import { PrivacyPolicyPage } from '../pages/PrivacyPolicyPage';
import { ChangePasswordPage } from '../pages/ChangePasswordPage';
import { ForbiddenPage, NotFoundPage, RouteErrorPage } from '../pages/ErrorPages';
import { PlaceholderPage } from '../pages/PlaceholderPage';
import { CatalogPage } from '../features/catalog/CatalogPage';
import { StaffPage } from '../features/staff/StaffPage';
import { ConfigPage } from '../features/config/ConfigPage';
import { PrivacyRequestsPage } from '../features/privacy/PrivacyRequestsPage';
import { EarningRulesPage } from '../features/earning/EarningRulesPage';
import { ReconPage } from '../features/recon/ReconPage';
import { TiersPage } from '../features/tiers/TiersPage';
import { RewardsPage } from '../features/rewards/RewardsPage';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { CampaignsPage } from '../features/campaigns/CampaignsPage';
import { ContentsPage } from '../features/contents/ContentsPage';
import { CustomerCareGroupsPage } from '../features/customer-care/CustomerCareGroupsPage';
import { AppLinksPage } from '../features/app-links/AppLinksPage';
import { ReservationSlotsPage } from '../features/reservation-slots/ReservationSlotsPage';
import { MembersPage } from '../features/members/MembersPage';
import { MemberProfilePage } from '../features/members/MemberProfilePage';
import { AuditLogsPage } from '../features/audit/AuditLogsPage';
import { ReviewsPage } from '../features/reviews/ReviewsPage';
import { navItems } from './navigation';

/**
 * Sprint dựng từng màn — lấy từ `knowledge-base/10-ui-screens.md`.
 * Hiện lên trang tạm để người mở màn biết ngay khi nào tới lượt và task nào.
 */
const plannedIn: Record<string, string> = {
  // Bốc số hoãn khỏi Phase 1 (họp 16/08). S6-Web-01 nay là "Khung giờ đặt bàn" (a-slots), không
  // còn là màn hàng chờ này.
  'a-queue': 'Hoãn Phase 2 (bốc số)',
  'a-tier': 'Đang phát triển',
  'a-reward': 'Đang phát triển',
};

/** Màn đã dựng thật. Còn lại rơi vào `PlaceholderPage` theo `plannedIn`. */
const builtScreens: Record<string, ReactElement> = {
  'a-dash': <DashboardPage />,
  'a-branches': <CatalogPage />,
  'a-slots': <ReservationSlotsPage />,
  'a-crm': <MembersPage />,
  'a-staff': <StaffPage />,
  'a-sysconfig': <ConfigPage />,
  'a-app-links': <AppLinksPage />,
  'a-privacy': <PrivacyRequestsPage />,
  'a-earning': <EarningRulesPage />,
  'a-recon': <ReconPage />,
  'a-tier': <TiersPage />,
  'a-reward': <RewardsPage />,
  'a-campaign': <CampaignsPage />,
  'a-content': <ContentsPage />,
  'a-care-groups': <CustomerCareGroupsPage />,
  'a-audit': <AuditLogsPage />,
  'a-review': <ReviewsPage />,
};

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/ho-tro', element: <SupportPage /> },
      { path: '/privacy', element: <PrivacyPolicyPage /> },
      { path: '/khong-du-quyen', element: <ForbiddenPage /> },
      {
        element: <RequireAuth />,
        children: [
          // Nằm TRONG RequireAuth nhưng NGOÀI AppLayout: người đang nợ đổi mật khẩu bị
          // server chặn mọi endpoint khác, nên thanh điều hướng chỉ dẫn họ tới ngõ cụt.
          { path: '/doi-mat-khau', element: <ChangePasswordPage /> },
          {
            element: <AppLayout />,
            children: [
              ...navItems.map((item) => ({
                path: item.path,
                element: builtScreens[item.screenId] ?? (
                  <PlaceholderPage
                    title={item.label}
                    screenId={item.screenId}
                    plannedIn={plannedIn[item.screenId] ?? 'chưa xếp sprint'}
                  />
                ),
              })),
              { path: '/members/:memberId', element: <MemberProfilePage /> },
              { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
]);
